import { Badge } from "@components/ui/Badge"
import { Button } from "@components/ui/Button"
import { PageError } from "@components/ui/PageError"
import { useToast } from "@components/ui/Toaster"
import { useClassroomResults, useDownloadGradesExport } from "@features/classrooms"
import { useCutFormat } from "@hooks/useCutFormat"
import { useGradeScale } from "@hooks/useGradeScale"
import { cn } from "@lib/cn"
import { saveBlob } from "@lib/download"
import { formatDate, formatGrade } from "@lib/intl"
import { logger } from "@lib/logger"
import { DEFAULT_TYPE_ICON, materialTypeLabel, TYPE_ICONS } from "@lib/materials"
import { ChevronDown, ChevronRight, Download } from "lucide-react"
import {
  forwardRef,
  memo,
  type ReactNode,
  useCallback,
  useEffect,
  useId,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react"
import { useTranslation } from "react-i18next"
import { Link } from "react-router-dom"

import { defaultResultsKey, selectedResultsKey } from "../resultsScope"
import { RESULTS_ALL_CUTS } from "../rosterState"

import { apiErrorCode, apiErrorKeys } from "@/lib/apiError"
import type { ClassroomResultsMaterial, CutSummary, EvaluationCut } from "@/types"

/** Work in a scope beyond its materials: tasks from the retired gradebook. */
const legacyIn = (summary: CutSummary | undefined): number => summary?.legacy_count ?? 0

/** Materials listed before "Ver los N": three on a phone keeps the roster —
 *  the page's own subject — near the fold (PRD-59); six from `md` up. */
const PHONE_LIMIT = 3
const DESKTOP_LIMIT = 6

interface Scope {
  /** `?cut=` value: the cut number as a string, or RESULTS_ALL_CUTS. */
  key: string
  cut: EvaluationCut | null
  summary: CutSummary
}

/** Lets the roster's "Cambiar" line open this section on a phone. */
export interface ClassroomResultsSectionHandle {
  /** Open the phone disclosure, bring the section into view, focus it. */
  reveal: () => void
}

interface Props {
  classroomId: string
  /** For the file name when the server's Content-Disposition is not readable. */
  classroomName: string
  schoolYear: string
  /** Analytics only: the kardex has one row per active student. */
  studentCount: number
  /** Raw `?cut=` value; null = the default scope. */
  cutParam: string | null
  onCutParamChange: (cut: string | null) => void
}

/**
 * Resultados (PRD-61 §FR-3): how the group is doing per evaluation cut and
 * over the whole course, the materials behind each figure, and the kardex
 * download. Replaces PRD-58's "Resultados por material" strip.
 *
 * On screen, the simple thing: one row per cut, each a radio. In the file, the
 * full student × material matrix — that matrix never comes on screen (PRD-61
 * Non-goals, PRD-58's retired gradebook).
 *
 * Below `md` the section is ONE disclosure whose closed state still carries
 * both figures (the cut and the whole course): open, it is ~680px, and the
 * roster — the page's own subject — sat 1,300px down at 375. It opens by
 * itself when `?cut=` is in the URL, because that link (Home's closing-cut
 * notice, a shared URL) was followed to read exactly this.
 *
 * Averages carry no pass/fail colour: they are Platonica's figure over what is
 * graded here, not the official grade, and the caption says so in words.
 */
export const ClassroomResultsSection = memo(
  forwardRef<ClassroomResultsSectionHandle, Props>(function ClassroomResultsSection(
    { classroomId, classroomName, schoolYear, studentCount, cutParam, onCutParamChange },
    ref,
  ) {
    const { t } = useTranslation(["students", "materials", "errors"])
    const toast = useToast()
    const scale = useGradeScale()
    const cutFormat = useCutFormat()
    const query = useClassroomResults(classroomId)
    const download = useDownloadGradesExport()
    const titleId = useId()
    const bodyId = useId()
    const listTitleId = useId()
    const radioRefs = useRef<(HTMLButtonElement | null)[]>([])
    const [expanded, setExpanded] = useState(false)
    // Phone only; `md` and up always shows the body. Remounted per classroom.
    const [openOnPhone, setOpenOnPhone] = useState(() => cutParam !== null)
    const sectionRef = useRef<HTMLElement>(null)
    const disclosureRef = useRef<HTMLButtonElement>(null)
    // Set by `reveal()`, consumed after the render that shows the body: a
    // radio inside a still-`display: none` body cannot take focus.
    const [focusChecked, setFocusChecked] = useState(false)

    useImperativeHandle(
      ref,
      () => ({
        reveal: () => {
          setOpenOnPhone(true)
          setFocusChecked(true)
          const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches
          sectionRef.current?.scrollIntoView({
            block: "start",
            behavior: reduceMotion ? "auto" : "smooth",
          })
        },
      }),
      [],
    )

    const data = query.data

    // Chronological cuts, then the whole course — the order of a boleta.
    const scopes = useMemo<Scope[]>(() => {
      if (!data) return []
      return [
        ...data.cuts.map((cut) => ({ key: String(cut.number), cut, summary: cut.summary })),
        { key: RESULTS_ALL_CUTS, cut: null, summary: data.total },
      ]
    }, [data])

    // Default: the cut that contains today; otherwise the whole course.
    // Same rule the roster rows use (resultsScope.ts), so both read one scope.
    const defaultKey = defaultResultsKey(data)
    const selectedKey = selectedResultsKey(data, cutParam)
    const selected = scopes.find((s) => s.key === selectedKey)

    const scopeMaterials = useMemo<ClassroomResultsMaterial[]>(() => {
      if (!data) return []
      if (selectedKey === RESULTS_ALL_CUTS) return data.materials
      const number = Number(selectedKey)
      return data.materials.filter((m) => m.cut === number)
    }, [data, selectedKey])

    // A new scope starts collapsed; "Ver los N" belonged to the previous list.
    useEffect(() => setExpanded(false), [selectedKey])

    useEffect(() => {
      if (query.isError) logger.warning("roster.results_failed", { classroomId })
    }, [query.isError, classroomId])

    // Focus lands on the CHECKED radio — the control "Cambiar" was about — so
    // the arrow keys change the scope at once. With no radios (no calendar)
    // the disclosure is the next best place.
    useEffect(() => {
      if (!focusChecked || !openOnPhone) return
      setFocusChecked(false)
      const checked = radioRefs.current[scopes.findIndex((s) => s.key === selectedKey)]
      ;(checked ?? disclosureRef.current)?.focus({ preventScroll: true })
    }, [focusChecked, openOnPhone, scopes, selectedKey])

    const scopeLabel = useCallback(
      (scope: Scope) => (scope.cut ? cutFormat.label(scope.cut) : cutFormat.wholeCourse()),
      [cutFormat],
    )

    // The closed phone line: the selected cut (else the current one) and the
    // whole course — "Parcial 3: 8,0 hasta hoy · Todo el curso: 7,5".
    const phoneSummary = useMemo(() => {
      if (!data) return null
      const figure = (value: number | null) =>
        value === null ? t("grid.dash") : formatGrade(value, scale)
      const course = t("results.summary.figure", {
        scope: cutFormat.wholeCourse(),
        value: figure(data.total.avg_grade),
      })
      const cutScope = selected?.cut ? selected : scopes.find((s) => s.key === defaultKey)
      if (!cutScope?.cut) return course
      const cut = t(
        cutScope.cut.state === "current"
          ? "results.summary.figure_so_far"
          : "results.summary.figure",
        { scope: cutFormat.label(cutScope.cut), value: figure(cutScope.summary.avg_grade) },
      )
      return t("results.summary.pair", { first: cut, second: course })
    }, [data, selected, scopes, defaultKey, cutFormat, scale, t])

    const select = useCallback(
      (key: string) => {
        if (key === selectedKey) return
        // Defaults are stripped on write, like every other roster param.
        onCutParamChange(key === defaultKey ? null : key)
        logger.info("roster.results_scope_selected", { classroomId, cut: key })
      },
      [selectedKey, defaultKey, onCutParamChange, classroomId],
    )

    // Arrow keys move the selection AND the focus (a radiogroup's contract);
    // only the checked radio is a tab stop.
    const onRadioKeyDown = (event: React.KeyboardEvent, index: number) => {
      const last = scopes.length - 1
      const target =
        event.key === "ArrowDown" || event.key === "ArrowRight"
          ? index === last
            ? 0
            : index + 1
          : event.key === "ArrowUp" || event.key === "ArrowLeft"
            ? index === 0
              ? last
              : index - 1
            : event.key === "Home"
              ? 0
              : event.key === "End"
                ? last
                : null
      if (target === null) return
      event.preventDefault()
      const scope = scopes[target]
      if (!scope) return
      select(scope.key)
      radioRefs.current[target]?.focus()
    }

    const handleDownload = () => {
      if (!selected) return
      const scope = selected.key === RESULTS_ALL_CUTS ? RESULTS_ALL_CUTS : Number(selected.key)
      download.mutate(
        { id: classroomId, scope, studentCount },
        {
          onSuccess: ({ blob, filename }) => {
            saveBlob(
              blob,
              // The server names the file; this is the same name, for when the
              // Content-Disposition header does not survive the cross-origin hop.
              filename ??
                t("results.download.filename", {
                  classroom: classroomName,
                  scope: scopeLabel(selected),
                  year: schoolYear,
                }),
            )
            toast.success(t("results.download.success"))
            logger.info("roster.grades_exported", { classroomId, cut: scope, bytes: blob.size })
          },
          onError: (err) => {
            toast.error(t(...apiErrorKeys(err, "grades_export_failed")))
            logger.error("roster.grades_export_failed", {
              classroomId,
              cut: scope,
              code: apiErrorCode(err),
            })
          },
        },
      )
    }

    // Nothing graded here yet — no sent gradeable material and no task kept
    // from the retired gradebook: no figure to show and no file worth
    // downloading. A classroom with only gradebook grades still has both.
    if (data && data.total.material_count + legacyIn(data.total) === 0) return null

    const hasCalendar = !!data?.period && data.cuts.length > 0
    const total = scopeMaterials.length
    const scopeLegacy = legacyIn(selected?.summary)
    const collapsed = !expanded

    return (
      <section
        ref={sectionRef}
        aria-labelledby={titleId}
        className="rounded-card border-border bg-surface scroll-mt-4 border p-4"
      >
        {/* Phone: the accordion shape — a heading that holds the button — so
          the section still reads as "Resultados" in a heading list. */}
        <h2 className="md:hidden">
          <button
            ref={disclosureRef}
            type="button"
            aria-expanded={openOnPhone}
            aria-controls={bodyId}
            onClick={() => {
              setOpenOnPhone((open) => !open)
              logger.info("roster.results_toggled", { classroomId, open: !openOnPhone })
            }}
            className="rounded-control flex min-h-11 w-full items-center gap-2 text-left"
          >
            <span className="min-w-0 flex-1">
              <span className="text-ink block text-sm font-semibold">{t("results.title")}</span>
              {phoneSummary ? (
                <span data-numeric className="text-meta block">
                  {phoneSummary}
                </span>
              ) : null}
            </span>
            {/* The only motion here, and it reports state (SubjectCard's
              Historial disclosure uses the same mark). */}
            <ChevronDown
              className={cn(
                "text-ink-subtle h-4 w-4 shrink-0 transition-transform duration-200",
                openOnPhone && "rotate-180",
              )}
              aria-hidden
            />
          </button>
        </h2>
        <h2 id={titleId} className="text-ink text-sm font-semibold max-md:hidden">
          {t("results.title")}
        </h2>
        <div id={bodyId} className={cn(!openOnPhone && "max-md:hidden")}>
          <p className="text-meta mt-0.5 max-md:mt-2">{t("results.caption")}</p>

          {query.isError && !data ? (
            <PageError
              className="py-6"
              message={t("results.error")}
              onRetry={() => void query.refetch()}
            />
          ) : !data ? (
            <ul className="mt-3 space-y-2" aria-hidden>
              {Array.from({ length: 3 }, (_, i) => (
                <li key={i} className="skeleton h-14" />
              ))}
            </ul>
          ) : (
            <>
              {hasCalendar ? (
                <div
                  role="radiogroup"
                  aria-label={t("results.scope_label")}
                  className="rounded-control border-border divide-hairline mt-3 divide-y overflow-hidden border"
                >
                  {scopes.map((scope, index) => (
                    <ScopeRow
                      key={scope.key}
                      buttonRef={(node) => {
                        radioRefs.current[index] = node
                      }}
                      label={scopeLabel(scope)}
                      when={
                        scope.cut
                          ? scope.cut.state === "current"
                            ? cutFormat.closes(scope.cut.end_date)
                            : cutFormat.range(scope.cut.start_date, scope.cut.end_date)
                          : data.period
                            ? cutFormat.range(data.period.start_date, data.period.end_date)
                            : null
                      }
                      summary={scope.summary}
                      soFar={scope.cut?.state === "current"}
                      upcoming={scope.cut?.state === "upcoming"}
                      checked={scope.key === selectedKey}
                      onSelect={() => select(scope.key)}
                      onKeyDown={(event) => onRadioKeyDown(event, index)}
                      average={
                        scope.summary.avg_grade === null
                          ? t("grid.dash")
                          : formatGrade(scope.summary.avg_grade, scale)
                      }
                    />
                  ))}
                </div>
              ) : (
                <>
                  {/* One scope is not a choice: a static row, not a lone radio. */}
                  <div className="rounded-control border-border mt-3 border">
                    <ScopeFigures
                      label={cutFormat.wholeCourse()}
                      when={null}
                      summary={data.total}
                      soFar={false}
                      upcoming={false}
                      checked={false}
                      average={
                        data.total.avg_grade === null
                          ? t("grid.dash")
                          : formatGrade(data.total.avg_grade, scale)
                      }
                    />
                  </div>
                  {/* No CTA: the calendar is the school's to capture, not hers. */}
                  <p className="text-meta mt-2">{t("results.no_calendar")}</p>
                </>
              )}

              <h3 id={listTitleId} className="text-label mt-4">
                {selected?.cut
                  ? t("results.materials.title_cut", { cut: scopeLabel(selected) })
                  : t("results.materials.title_all")}
              </h3>
              {total === 0 && scopeLegacy === 0 ? (
                <p className="text-meta mt-1">
                  {t("results.materials.empty", { cut: selected ? scopeLabel(selected) : "" })}
                </p>
              ) : total === 0 ? null : (
                <ul aria-labelledby={listTitleId} className="divide-hairline mt-1 divide-y">
                  {scopeMaterials.map((material, index) => (
                    <MaterialResultRow
                      key={material.id}
                      material={material}
                      showManual={selectedKey !== RESULTS_ALL_CUTS}
                      className={cn(
                        collapsed && index >= DESKTOP_LIMIT && "hidden",
                        collapsed &&
                          index >= PHONE_LIMIT &&
                          index < DESKTOP_LIMIT &&
                          "max-md:hidden",
                      )}
                      classroomId={classroomId}
                    />
                  ))}
                </ul>
              )}
              {/* The gradebook's tasks count in the figures above but have no
                material to open, so they are named here instead of listed. */}
              {scopeLegacy > 0 ? (
                <p className="text-meta mt-1">
                  {t("results.materials.legacy_note", { count: scopeLegacy })}
                </p>
              ) : null}
              {total > PHONE_LIMIT && (
                <Button
                  variant="ghost"
                  size="sm"
                  className={cn("-mx-2.5 mt-1", collapsed && total <= DESKTOP_LIMIT && "md:hidden")}
                  aria-expanded={expanded}
                  onClick={() => setExpanded((open) => !open)}
                >
                  {expanded ? t("results.show_fewer") : t("results.show_all", { count: total })}
                </Button>
              )}

              {/* An empty scope (an upcoming cut) would download a file of bare
              names: no button until something counts toward it. */}
              {total > 0 || scopeLegacy > 0 ? (
                <div className="border-hairline mt-3 flex flex-col gap-2 border-t pt-3 sm:flex-row sm:items-center">
                  <Button
                    variant="outline"
                    onClick={handleDownload}
                    loading={download.isPending}
                    leftIcon={<Download className="h-4 w-4" aria-hidden />}
                    className="w-full sm:w-auto"
                  >
                    {t("results.download.cta")}
                  </Button>
                  <p className="text-meta">
                    {t("results.download.hint", { scope: selected ? scopeLabel(selected) : "" })}
                  </p>
                </div>
              ) : null}
            </>
          )}
        </div>
      </section>
    )
  }),
)

interface FiguresProps {
  /** The radio mark, on the selectable rows only. */
  leading?: ReactNode
  label: string
  /** "cierra 31 oct" for the current cut, a range otherwise. */
  when: string | null
  summary: CutSummary
  soFar: boolean
  upcoming: boolean
  checked: boolean
  average: string
}

/** The content of one scope row — shared by the radio and the static row. */
function ScopeFigures({
  leading,
  label,
  when,
  summary,
  soFar,
  upcoming,
  checked,
  average,
}: FiguresProps) {
  const { t } = useTranslation("students")
  const legacy = legacyIn(summary)
  // "3 materiales · 2 trabajos anteriores · 16/36 calificados": what the
  // average rests on (9.2 over 5 of 120 cells is not 9.2 over 118 of 120).
  // Each part appears only when it is true, so a gradebook-only scope never
  // reads "0 materiales"; parts join through an i18n template, not by hand.
  const parts = [
    summary.material_count > 0 || legacy === 0
      ? t("results.row.materials", { count: summary.material_count })
      : null,
    legacy > 0 ? t("results.row.legacy", { count: legacy }) : null,
    summary.expected > 0
      ? t("results.row.graded", { graded: summary.graded, expected: summary.expected })
      : null,
  ].filter((part): part is string => part !== null)
  const rowFacts =
    upcoming && summary.material_count === 0 && legacy === 0
      ? t("results.row.not_started")
      : parts.reduce((first, second) => t("results.summary.pair", { first, second }))
  // On the accent-tinted checked row, secondary text takes the accent's ink
  // rather than grey (grey on a tinted ground reads as disabled).
  // Same size and leading as `text-meta`, so the checked row never changes
  // height and the list below does not jump while arrowing through it.
  const meta = checked ? "text-accent-ink text-xs leading-4.5" : "text-meta"
  return (
    <span className="flex min-h-11 w-full items-center gap-3 px-3 py-2 text-left">
      {leading}
      <span className="min-w-0 flex-1">
        <span className="flex flex-wrap items-baseline gap-x-1.5">
          <span
            className={cn(
              "text-sm font-medium",
              checked ? "text-accent-ink" : upcoming ? "text-ink-muted" : "text-ink",
            )}
          >
            {label}
          </span>
          {when ? <span className={meta}>{when}</span> : null}
        </span>
        <span className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1">
          <span className={meta}>{rowFacts}</span>
          {summary.pending_review > 0 ? (
            <Badge variant="attention">
              {t("results.pending_review", { count: summary.pending_review })}
            </Badge>
          ) : null}
        </span>
      </span>
      <span className="shrink-0 text-right">
        <span
          data-numeric
          className={cn(
            "block text-base font-semibold",
            checked ? "text-accent-ink" : upcoming ? "text-ink-subtle" : "text-ink",
          )}
        >
          <span className="sr-only">{t("results.row.average_label")} </span>
          {average}
        </span>
        {soFar ? <span className={cn("block", meta)}>{t("results.row.so_far")}</span> : null}
      </span>
    </span>
  )
}

interface ScopeRowProps extends FiguresProps {
  /** Not `ref`: React 18 strips that prop from function components. */
  buttonRef: (node: HTMLButtonElement | null) => void
  onSelect: () => void
  onKeyDown: (event: React.KeyboardEvent) => void
}

function ScopeRow({ buttonRef, onSelect, onKeyDown, ...figures }: ScopeRowProps) {
  return (
    <button
      ref={buttonRef}
      type="button"
      role="radio"
      aria-checked={figures.checked}
      tabIndex={figures.checked ? 0 : -1}
      onClick={onSelect}
      onKeyDown={onKeyDown}
      className={cn(
        // Inset outline: the group clips its corners (`overflow-hidden`), which
        // would otherwise cut the global 2px-offset focus ring.
        "flex w-full transition-colors duration-150 focus-visible:-outline-offset-2",
        figures.checked ? "bg-accent-soft" : "hover:bg-inset",
      )}
    >
      <ScopeFigures
        {...figures}
        leading={
          // The standard radio mark: selection must not be colour alone
          // (accessibility.md §5), and a hollow/filled circle is the one
          // shape every phone user already reads as "pick one".
          <span
            aria-hidden
            className={cn(
              "flex h-4 w-4 shrink-0 items-center justify-center rounded-full border-2",
              figures.checked ? "border-accent" : "border-border-strong",
            )}
          >
            {figures.checked ? <span className="bg-accent h-2 w-2 rounded-full" /> : null}
          </span>
        }
      />
    </button>
  )
}

const MaterialResultRow = memo(function MaterialResultRow({
  material,
  showManual,
  className,
  classroomId,
}: {
  material: ClassroomResultsMaterial
  /** "Movido a mano" only means something inside a cut's list. */
  showManual: boolean
  className?: string
  classroomId: string
}) {
  const { t } = useTranslation(["students", "materials", "common"])
  const scale = useGradeScale()
  const Icon = TYPE_ICONS[material.material_type] ?? DEFAULT_TYPE_ICON
  const type = materialTypeLabel(t, material.material_type)
  const average =
    material.avg_grade === null ? t("grid.dash") : formatGrade(material.avg_grade, scale)
  const graded = t("results.graded", { graded: material.graded, total: material.expected })

  return (
    <li className={cn("relative", className)}>
      {/* Stretched link (the ClassroomCard pattern): the whole row opens the
          material, and the review link sits above it as its own control —
          two targets, never a link inside a link. */}
      <Link
        to={`/materiales/${material.id}`}
        aria-label={t("results.material.open_aria", {
          title: material.title,
          average,
          graded,
        })}
        className="rounded-control hover:bg-inset absolute -inset-x-2 inset-y-0 transition-colors"
      />
      <div className="pointer-events-none relative flex min-h-11 items-center gap-3 py-2">
        <Icon className="text-ink-subtle h-4 w-4 shrink-0" aria-hidden />
        <span className="min-w-0 flex-1">
          <span className="text-ink block truncate text-sm">{material.title}</span>
          {/* Wraps rather than truncates: at 375px the graded count is the
              part a truncation would cut, and it is the part she came for. */}
          <span className="text-meta block">
            {t(
              showManual && material.cut_is_manual
                ? "results.material.meta_manual"
                : "results.material.meta",
              { type, date: formatDate(material.due_date, t("common:cuts.day_format")), graded },
            )}
          </span>
          {material.pending_review > 0 ? (
            <Link
              to={`/materiales/${material.id}/revision`}
              onClick={() =>
                logger.info("materials.review_queue_opened", {
                  material_id: material.id,
                  classroom_id: classroomId,
                  pending_review_count: material.pending_review,
                  source: "roster_results",
                })
              }
              aria-label={t("results.material.review_aria", {
                count: material.pending_review,
                title: material.title,
              })}
              className="rounded-control pointer-events-auto relative mt-0.5 inline-flex min-h-11 items-center md:min-h-8"
            >
              <Badge variant="attention">
                {t("results.pending_review", { count: material.pending_review })}
              </Badge>
            </Link>
          ) : null}
        </span>
        <span data-numeric className="text-ink shrink-0 text-sm font-semibold">
          {average}
        </span>
        <ChevronRight className="text-ink-subtle h-4 w-4 shrink-0" aria-hidden />
      </div>
    </li>
  )
})
